import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import {
  clearDraft,
  draftKey,
  getDraft,
  getSessionEpoch,
  hasPendingSaves,
  saveDraft,
  trackPendingSave,
} from "@/hooks/autosaveRegistry";
import { getCurrentUser } from "@/services/api/auth";

/**
 * JUB-152 — save as you go.
 *
 * Sends only the fields that differ from the last server-acknowledged
 * snapshot (the baseline), debounced while typing and flushed on blur, tab
 * hide, page hide, reconnect and unmount. One request in flight per form;
 * edits made meanwhile are coalesced into one follow-up request. Transient
 * failures retry a bounded number of times, then the edits stay on screen with
 * an explicit retry — they are never discarded silently. Validation errors
 * (client or 422) withhold just the offending fields and never show "saved".
 */

export type AutosaveStatus = "idle" | "pending" | "saving" | "saved" | "invalid" | "error" | "offline";

type Plain = Record<string, unknown>;

export interface UseAutosaveOptions<T extends Plain, R> {
  /** Payload in server shape, built from the current UI state. */
  current: T;
  /** Server snapshot the form starts from; null until loaded. Read once per `resetKey`. */
  initial: T | null;
  /** Re-seeds the baseline when it changes (e.g. the event uuid). */
  resetKey?: string;
  /** Registry key for in-flight tracking (the event uuid). */
  pendingKey?: string;
  /**
   * Names this form for draft retention: edits still unacknowledged when the
   * form unmounts are kept (per account + event + form) and offered back on
   * return, with an explicit retry. Requires `pendingKey`.
   */
  formId?: string;
  /** Top-level keys this form may ever send; anything else is stripped. */
  allowedKeys: readonly (keyof T & string)[];
  save: (patch: Partial<T>) => Promise<R>;
  /** Called with the server response and the patch it acknowledged. */
  onSaved?: (response: R, patch: Partial<T>) => void;
  /** Client validation: dot paths → message. Invalid paths are withheld. */
  validate?: (current: T) => Record<string, string>;
  /** Top-level object keys the server replaces whole (e.g. heroText {ro,en}): sent complete when any part changes. */
  atomicKeys?: readonly (keyof T & string)[];
  /**
   * Top-level keys the server validates together (e.g. event type + baby
   * names): when one is sent, all are sent; when one is withheld, all are.
   */
  groups?: readonly (readonly (keyof T & string)[])[];
  /** Top-level keys sent only on flush (blur), not while typing. */
  blurOnlyKeys?: readonly (keyof T & string)[];
  debounceMs?: number;
  maxAttempts?: number;
  enabled?: boolean;
}

export interface UseAutosaveResult {
  status: AutosaveStatus;
  /** Client + server validation errors, by dot path. */
  fieldErrors: Record<string, string>;
  /** Send every pending change now (blur-only keys included). */
  flush: () => Promise<void>;
  /** Send immediately once the next edit renders (switches, selects). */
  flushOnNextChange: () => void;
  /** Explicit retry after the automatic attempts ran out. */
  retry: () => Promise<void>;
  /** True while there are edits the server has not acknowledged. */
  dirty: boolean;
  /** Unsaved edits from an earlier visit were restored; retry or discard them. */
  restored: boolean;
  /** Forget the restored draft (the caller reloads the server state). */
  discardDraft: () => void;
}

/**
 * The draft a form left behind for this account + event, if any (JUB-152).
 * Callers merge it over the freshly loaded server state before seeding.
 */
export function readAutosaveDraft(eventUuid: string | undefined, formId: string): Record<string, unknown> | undefined {
  const account = getCurrentUser()?.uuid;
  if (!account || !eventUuid) return undefined;
  return getDraft(draftKey(account, eventUuid, formId));
}

export const AUTOSAVE_DEBOUNCE_MS = 800;
export const AUTOSAVE_MAX_ATTEMPTS = 5;
const BACKOFF_CAP_MS = 30_000;

// ---------------------------------------------------------------- helpers

function isPlainObject(value: unknown): value is Plain {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, i) => deepEqual(item, b[i]));
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const key of keys) if (!deepEqual(a[key], b[key])) return false;
    return true;
  }
  return false;
}

/** Changed leaves of `current` vs `base`; plain objects are diffed per key. */
export function diffPatch(base: unknown, current: unknown): unknown {
  if (isPlainObject(base) && isPlainObject(current)) {
    const out: Plain = {};
    for (const key of Object.keys(current)) {
      const sub = diffPatch(base[key], current[key]);
      if (sub !== undefined) out[key] = sub;
    }
    return Object.keys(out).length ? out : undefined;
  }
  return deepEqual(base, current) ? undefined : current;
}

function mergePatch<T>(base: T, patch: unknown): T {
  if (!isPlainObject(base) || !isPlainObject(patch)) return patch as T;
  const out: Plain = { ...base };
  for (const [key, value] of Object.entries(patch)) {
    out[key] = isPlainObject(value) && isPlainObject(out[key]) ? mergePatch(out[key], value) : value;
  }
  return out as T;
}

function getAt(obj: unknown, path: string): unknown {
  let cur: unknown = obj;
  for (const part of path.split(".")) {
    if (!isPlainObject(cur) || !(part in cur)) return undefined;
    cur = cur[part];
  }
  return cur;
}

function hasPath(obj: unknown, path: string): boolean {
  let cur: unknown = obj;
  for (const part of path.split(".")) {
    if (!isPlainObject(cur) || !(part in cur)) return false;
    cur = cur[part];
  }
  return true;
}

/** Remove `path` from a patch, dropping parents left empty. */
function omitPath(patch: Plain, path: string): void {
  const [head, ...rest] = path.split(".");
  if (!(head in patch)) return;
  if (rest.length === 0) {
    delete patch[head];
    return;
  }
  const child = patch[head];
  if (!isPlainObject(child)) return;
  const copy = { ...child };
  omitPath(copy, rest.join("."));
  if (Object.keys(copy).length) patch[head] = copy;
  else delete patch[head];
}

function httpStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status;
}

function validationErrors(error: unknown): Record<string, string> {
  const data = (error as { response?: { data?: Plain } })?.response?.data;
  const raw = (isPlainObject(data?.error) ? (data?.error as Plain).errors : undefined) ?? data?.errors;
  const out: Record<string, string> = {};
  if (isPlainObject(raw)) {
    for (const [path, messages] of Object.entries(raw)) {
      out[path] = Array.isArray(messages) ? String(messages[0] ?? "") : String(messages ?? "");
    }
  }
  return out;
}

function isOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// ---------------------------------------------------------------- hook

export function useAutosave<T extends Plain, R = unknown>(options: UseAutosaveOptions<T, R>): UseAutosaveResult {
  const { t } = useTranslation();
  // Everything else is read through optionsRef, so callbacks see the latest.
  const { current, initial, resetKey } = options;

  const optionsRef = useRef(options);
  optionsRef.current = options;
  const tRef = useRef(t);
  tRef.current = t;

  const baselineRef = useRef<T | null>(null);
  const seededKeyRef = useRef<string | undefined>(undefined);
  const generationRef = useRef(0);
  const inFlightRef = useRef<Promise<void> | null>(null);
  const queuedRef = useRef<"none" | "debounced" | "flush">("none");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const failedRef = useRef(false);
  const offlineRef = useRef(false);
  const savedOnceRef = useRef(false);
  /** 422s: path → the value the server refused; withheld until it changes. */
  const serverErrorsRef = useRef<Record<string, { message: string; value: unknown }>>({});

  const [status, setStatus] = useState<AutosaveStatus>("idle");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState(false);
  const [restored, setRestored] = useState(false);
  /**
   * A draft recovered from an earlier visit waits for the user's choice:
   * nothing sends it automatically (debounce, blur, hide, online, unmount)
   * until Retry (one explicit save) or Discard. Edits made meanwhile join the
   * draft and stay blocked too; the chip keeps saying so.
   */
  const recoveredRef = useRef(false);
  /** The account and session this form's edits belong to, captured when it seeds. */
  const sessionRef = useRef<{ account: string | null; epoch: number }>({ account: null, epoch: -1 });

  const captureSession = useCallback(() => {
    sessionRef.current = { account: getCurrentUser()?.uuid ?? null, epoch: getSessionEpoch() };
  }, []);
  /** False once the user logged out or another account logged in. */
  const sessionValid = useCallback(
    () =>
      sessionRef.current.epoch === getSessionEpoch() &&
      sessionRef.current.account !== null &&
      sessionRef.current.account === (getCurrentUser()?.uuid ?? null),
    [],
  );
  const currentDraftKey = useCallback((): string | null => {
    const { formId, pendingKey } = optionsRef.current;
    const { account } = sessionRef.current;
    return formId && pendingKey && account && sessionValid() ? draftKey(account, pendingKey, formId) : null;
  }, [sessionValid]);

  const clientErrors = useCallback((): Record<string, string> => {
    const { validate } = optionsRef.current;
    return validate ? validate(optionsRef.current.current) : {};
  }, []);

  const activeServerErrors = useCallback((): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const [path, { message, value }] of Object.entries(serverErrorsRef.current)) {
      if (deepEqual(getAt(optionsRef.current.current, path), value)) out[path] = message;
      else delete serverErrorsRef.current[path];
    }
    return out;
  }, []);

  /** Unacknowledged changes, without the withheld (invalid) paths. */
  const computePatch = useCallback(
    (includeBlurOnly: boolean, withholdInvalid = true): Partial<T> | null => {
      const baseline = baselineRef.current;
      if (!baseline) return null;
      const { allowedKeys, blurOnlyKeys = [], atomicKeys = [] } = optionsRef.current;
      const raw = diffPatch(baseline, optionsRef.current.current);
      if (!isPlainObject(raw)) return null;
      const patch: Plain = {};
      for (const [key, value] of Object.entries(raw)) {
        if (!allowedKeys.includes(key as keyof T & string)) {
          console.error(`[autosave] refusing to send key outside the allowlist: ${key}`);
          continue;
        }
        if (!includeBlurOnly && blurOnlyKeys.includes(key as keyof T & string)) continue;
        patch[key] = atomicKeys.includes(key as keyof T & string) ? optionsRef.current.current[key] : value;
      }
      const groups = optionsRef.current.groups ?? [];
      for (const group of groups) {
        if (group.some((key) => key in patch)) {
          for (const key of group) patch[key] = optionsRef.current.current[key];
        }
      }
      if (withholdInvalid) {
        const invalid = Object.keys({ ...clientErrors(), ...activeServerErrors() });
        for (const path of invalid) {
          omitPath(patch, path);
          const top = path.split(".")[0];
          for (const group of groups) if (group.includes(top as keyof T & string)) for (const key of group) delete patch[key];
        }
      }
      return Object.keys(patch).length ? (patch as Partial<T>) : null;
    },
    [clientErrors, activeServerErrors],
  );

  const refreshStatus = useCallback(() => {
    if (!mountedRef.current) return;
    const errors = { ...clientErrors(), ...activeServerErrors() };
    const unsaved = computePatch(true, false) !== null;
    setFieldErrors((prev) => (deepEqual(prev, errors) ? prev : errors));
    setDirty(unsaved);
    let next: AutosaveStatus;
    if (inFlightRef.current) next = "saving";
    else if ((failedRef.current || recoveredRef.current) && unsaved) next = "error";
    else if (offlineRef.current && unsaved) next = "offline";
    else if (Object.keys(errors).length) next = "invalid";
    else if (unsaved) next = "pending";
    else next = savedOnceRef.current ? "saved" : "idle";
    setStatus(next);
  }, [clientErrors, activeServerErrors, computePatch]);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // `send` and `schedule` reference each other; keep the latest in refs.
  const sendRef = useRef<(includeBlurOnly: boolean) => Promise<void>>(() => Promise.resolve());

  const schedule = useCallback(() => {
    clearTimer();
    if (!mountedRef.current || failedRef.current || recoveredRef.current) return;
    if (!computePatch(false)) return;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      void sendRef.current(false);
    }, optionsRef.current.debounceMs ?? AUTOSAVE_DEBOUNCE_MS);
  }, [computePatch]);

  const send = useCallback(
    (includeBlurOnly: boolean): Promise<void> => {
      if (!baselineRef.current || optionsRef.current.enabled === false) return Promise.resolve();
      if (recoveredRef.current || !sessionValid()) {
        refreshStatus();
        return Promise.resolve();
      }
      if (inFlightRef.current) {
        if (includeBlurOnly || queuedRef.current === "none") queuedRef.current = includeBlurOnly ? "flush" : "debounced";
        return inFlightRef.current;
      }
      clearTimer();
      if (!computePatch(includeBlurOnly)) {
        refreshStatus();
        return Promise.resolve();
      }
      if (isOffline()) {
        offlineRef.current = true;
        refreshStatus();
        return Promise.resolve();
      }

      const generation = generationRef.current;
      const run = async () => {
        let attempts = 0;
        let blur = includeBlurOnly;
        for (;;) {
          if (generation !== generationRef.current || !sessionValid()) return;
          const patch = computePatch(blur);
          if (!patch) return;
          if (isOffline()) {
            offlineRef.current = true;
            return;
          }
          try {
            const response = await optionsRef.current.save(patch);
            // Logged out (or another account) meanwhile: drop the result.
            if (generation !== generationRef.current || !sessionValid()) return;
            baselineRef.current = mergePatch(baselineRef.current as T, patch);
            failedRef.current = false;
            offlineRef.current = false;
            savedOnceRef.current = true;
            attempts = 0;
            optionsRef.current.onSaved?.(response, patch);
            const key = currentDraftKey();
            if (key) {
              const remaining = computePatch(true, false);
              if (!mountedRef.current && remaining) saveDraft(key, remaining as Plain);
              else clearDraft(key);
            }
            if (mountedRef.current) setRestored(false);
            if (queuedRef.current === "flush" || !mountedRef.current) {
              queuedRef.current = "none";
              blur = true;
              continue;
            }
            return;
          } catch (error) {
            if (generation !== generationRef.current || !sessionValid()) return;
            const code = httpStatus(error);
            if (code === 422) {
              const errors = validationErrors(error);
              const mapped = Object.keys(errors).filter((path) => hasPath(patch, path));
              if (mapped.length) {
                for (const path of mapped) {
                  serverErrorsRef.current[path] = { message: errors[path], value: getAt(patch, path) };
                }
                continue; // re-send what is left, without the refused fields
              }
              failedRef.current = true;
              return;
            }
            if (code === undefined && isOffline()) {
              offlineRef.current = true;
              return;
            }
            const retriable = code === undefined || code === 408 || code === 409 || code === 429 || code >= 500;
            attempts += 1;
            if (!retriable || attempts >= (optionsRef.current.maxAttempts ?? AUTOSAVE_MAX_ATTEMPTS)) {
              failedRef.current = true;
              if (!mountedRef.current && sessionValid()) toast.error(tRef.current("autosave.lostAfterLeave"));
              return;
            }
            refreshStatus();
            await sleep(Math.min(1000 * 2 ** (attempts - 1), BACKOFF_CAP_MS));
          }
        }
      };

      const promise: Promise<void> = run().finally(() => {
        if (inFlightRef.current === promise) inFlightRef.current = null;
        if (generation !== generationRef.current) return;
        const queued = queuedRef.current;
        queuedRef.current = "none";
        if (queued === "flush" && !failedRef.current) void sendRef.current(true);
        else if (mountedRef.current) schedule();
        refreshStatus();
      });
      inFlightRef.current = promise;
      if (optionsRef.current.pendingKey) trackPendingSave(optionsRef.current.pendingKey, promise);
      refreshStatus();
      return promise;
    },
    [computePatch, refreshStatus, schedule, currentDraftKey, sessionValid],
  );
  sendRef.current = send;

  const flush = useCallback(async () => {
    await send(true);
    while (inFlightRef.current) await inFlightRef.current;
  }, [send]);

  const retry = useCallback(async () => {
    // The user's explicit choice releases a recovered draft for one save.
    recoveredRef.current = false;
    failedRef.current = false;
    offlineRef.current = false;
    await flush();
  }, [flush]);

  const discardDraft = useCallback(() => {
    const key = currentDraftKey();
    if (key) clearDraft(key);
    recoveredRef.current = false;
    failedRef.current = false;
    setRestored(false);
    refreshStatus();
  }, [currentDraftKey, refreshStatus]);

  // Seed (or re-seed) the baseline from the server snapshot.
  const initialJson = initial === null ? null : JSON.stringify(initial);
  useEffect(() => {
    if (seededKeyRef.current !== resetKey) {
      seededKeyRef.current = resetKey;
      if (baselineRef.current) {
        generationRef.current++;
        baselineRef.current = null;
        serverErrorsRef.current = {};
        failedRef.current = false;
        recoveredRef.current = false;
        savedOnceRef.current = false;
        queuedRef.current = "none";
        inFlightRef.current = null; // a request for the previous key no longer blocks this one
        clearTimer();
      }
      refreshStatus();
    }
    if (!baselineRef.current && initialJson !== null) {
      baselineRef.current = JSON.parse(initialJson) as T;
      captureSession();
      // A draft left by an earlier visit (already merged into the form by the
      // caller) is shown as unsaved with an explicit retry — never auto-sent
      // behind the user's back, never "saved".
      const key = currentDraftKey();
      const hasDraft = !!key && !!getDraft(key);
      recoveredRef.current = hasDraft;
      setRestored(hasDraft);
      refreshStatus();
    }
  }, [resetKey, initialJson, refreshStatus, currentDraftKey, captureSession]);

  // Every edit: refresh status and (re)start the debounce.
  const flushNextRef = useRef(false);
  const flushOnNextChange = useCallback(() => {
    flushNextRef.current = true;
  }, []);

  const currentJson = JSON.stringify(current);
  useEffect(() => {
    if (!baselineRef.current) return;
    if (flushNextRef.current) {
      flushNextRef.current = false;
      void sendRef.current(true);
      return;
    }
    refreshStatus();
    if (!inFlightRef.current) schedule();
  }, [currentJson, refreshStatus, schedule]);

  // Flush when the page is hidden or closed, retry when back online, and warn
  // before unloading with unsaved edits.
  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState === "hidden") void sendRef.current(true);
    };
    const onPageHide = () => void sendRef.current(true);
    const onOnline = () => {
      offlineRef.current = false;
      void sendRef.current(true);
    };
    const onOffline = () => {
      offlineRef.current = true;
      refreshStatus();
    };
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (inFlightRef.current || computePatch(true, false)) {
        e.preventDefault();
        e.returnValue = tRef.current("autosave.unsavedWarning");
        return e.returnValue;
      }
      return undefined;
    };
    document.addEventListener("visibilitychange", onHidden);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      document.removeEventListener("visibilitychange", onHidden);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [computePatch, refreshStatus]);

  // Unmount (tab switch, route change): flush whatever is left. The request
  // outlives the form and is tracked in the registry so a reload waits for it.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimer();
      // Keep what the server has not acknowledged until a save confirms it.
      const key = currentDraftKey();
      const unsaved = computePatch(true, false);
      if (key && unsaved) saveDraft(key, unsaved as Plain);
      if (recoveredRef.current) return; // still waiting for the user's choice
      if (inFlightRef.current) queuedRef.current = "flush";
      else void sendRef.current(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { status, fieldErrors, flush, flushOnNextChange, retry, dirty, restored, discardDraft };
}

/** Status shown for several autosaving forms on one screen. */
export function combineAutosaveStatus(...statuses: AutosaveStatus[]): AutosaveStatus {
  const order: AutosaveStatus[] = ["error", "offline", "invalid", "saving", "pending", "saved", "idle"];
  for (const s of order) if (statuses.includes(s)) return s;
  return "idle";
}

export { hasPendingSaves };
